import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { motion } from 'framer-motion';
import DownloadIcon from '@mui/icons-material/Download';
import { uploadFormFile } from '../../../utils/apiUtils';
import { getSecureFileUrl } from '../../../utils/secureFileUtils';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

// API URL setup - get backend URL from environment variables
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;

// Get frontend URL (used to identify URLs that need to be replaced)
const FRONTEND_URL = window.location.origin; // e.g., 'http://localhost:3000' or 'https://apply.mngldodo.site'

// Force Backend URL - this ensures consistency regardless of environment variables
// For local: http://localhost:5000
// For production: Use the API URL from environment or default to frontend URL
const FORCE_BACKEND_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || FRONTEND_URL)
  : 'http://localhost:5000';

// Helper function to make sure URLs are always relative paths
// This ensures our file URLs will work regardless of environment (localhost or production domain)
const formatAsRelativePath = (url: string): string => {
  if (!url) return '';
  
  // If it's already a relative path starting with /, return as is
  if (url.startsWith('/')) return url;
  
  // If it's a full URL, extract just the path portion
  if (url.includes('://')) {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch (e) {
      console.error('Invalid URL:', url);
      // If URL parsing fails, try to extract the path after the domain
      const parts = url.split('/');
      if (parts.length > 2) {
        // Remove protocol and domain parts
        return '/' + parts.slice(3).join('/');
      }
      return url;
    }
  }
  
  // If it doesn't start with / and isn't a full URL, add the leading /
  return `/${url}`;
};

// Interface for file metadata
interface FileItem {
  fieldName: string;
  fieldLabel: string;
  filePath: string;
  filename: string;
  downloadUrl: string;
  previewUrl: string;
}

// Styled components for the file management UI
const Container = styled.div`
  width: 100%;
  margin-top: 1.5rem;
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(76, 110, 245, 0.1);
  padding: 1.5rem;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #1a2533;
`;

const FilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  grid-gap: 1rem;
  margin-top: 1.5rem;
`;

const FileCard = styled(motion.div)`
  background-color: white;
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  height: 100%;
  position: relative;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(76, 110, 245, 0.12);
    border-color: #4c6ef5;
  }
`;

const FileLabel = styled.h3`
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.75rem;
  font-size: 1rem;
`;

const FileName = styled.div`
  font-size: 0.85rem;
  color: #64748b;
  margin-bottom: 1rem;
  word-break: break-word;
  line-height: 1.4;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: auto;
`;

const ReplaceFileButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  border: none;
  gap: 5px;
  background-color: #e6f0ff;
  color: #4c6ef5;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background-color: #4c6ef5;
    color: white;
    transform: scale(1.05);
  }
  
  &.disabled {
    background-color: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
    &:hover {
      transform: none;
      background-color: #e2e8f0;
      color: #94a3b8;
    }
  }
`;

const ActionButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  background-color: #e6f0ff;
  color: #4c6ef5;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #4c6ef5;
    color: white;
  }
  
  &:disabled {
    background-color: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

const UploadInput = styled.input`
  display: none;
`;

const ZipDownloadButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  background-color: #4c6ef5;
  color: white;
  font-weight: 600;
  margin-top: 1.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #3b5bdb;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(76, 110, 245, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  
  p {
    margin-top: 1rem;
    color: #64748b;
  }
`;

const NoFiles = styled.div`
  padding: 2rem;
  text-align: center;
  color: #64748b;
  
  p {
    margin-bottom: 1rem;
  }
`;

// File management component

// Main component for managing application files
const ApplicationFiles: React.FC<{ applicationId: string }> = ({ applicationId }) => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadingField, setUploadingField] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadStatus, setUploadStatus] = useState<Record<string, 'idle' | 'uploading' | 'success' | 'error'>>({});
  const [uploadMessages, setUploadMessages] = useState<Record<string, string>>({});
  const [isApplicationLocked, setIsApplicationLocked] = useState<boolean>(true); // Default to locked for safety
  
  // Modal state for file preview
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    fileUrl: string;
    signedUrl: string; // Secure signed URL for display
    fileName: string;
    fileType: string;
    filePath: string; // Original file path for signing
    isLoading: boolean; // Loading state while fetching signed URL
  }>({ isOpen: false, fileUrl: '', signedUrl: '', fileName: '', fileType: '', filePath: '', isLoading: false });

  // Fetch files function that can be called to refresh data
  const fetchFiles = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Configure axios to send credentials (cookies)
      axios.defaults.withCredentials = true;
      
      const response = await axios.get(`${API_BASE}/api/applications/${applicationId}/files`);
      
      if (response.data.success) {
        // Fix URLs to ensure they are properly formatted for the current domain
        const correctedFiles = response.data.files.map((file: FileItem) => ({
          ...file,
          // Format URLs to be relative paths (without domain) so they work across environments
          // This ensures our handler functions can append the correct domain
          previewUrl: formatAsRelativePath(file.previewUrl),
          downloadUrl: formatAsRelativePath(file.downloadUrl)
        }));
        setFiles(correctedFiles);
      } else {
        setError('Failed to fetch files');
      }
    } catch (err: any) {
      console.error('Error fetching application files:', err);
      setError(err.response?.data?.message || 'Failed to fetch files');
    } finally {
      setLoading(false);
    }
  };
  
  // Check if application is locked
  const checkApplicationLockStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/applications/${applicationId}/lock-status`);
      if (response.data.success) {
        setIsApplicationLocked(response.data.isLocked);
        console.log('[ApplicationFiles] Lock status:', response.data.isLocked);
      }
    } catch (error) {
      console.error('[ApplicationFiles] Error checking lock status:', error);
      // Default to locked if we can't check
      setIsApplicationLocked(true);
    }
  };

  // Format files data and load on component mount
  useEffect(() => {    
    if (applicationId) {
      fetchFiles();
      checkApplicationLockStatus();
    }
  }, [applicationId]);

  // Sort files alphabetically by fieldLabel
  const sortedFiles = [...files].sort((a, b) => 
    a.fieldLabel.localeCompare(b.fieldLabel)
  );

  // Handle file preview - opens modal with signed URL using the same utility as ApplicationFormPage1
  const handlePreview = async (previewUrl: string, fileName: string) => {
    // Get the URL path without domain
    let pathOnly = previewUrl;
    
    // Extract path if it's a full URL
    if (previewUrl.includes('://')) {
      const url = new URL(previewUrl);
      pathOnly = url.pathname;
    } else if (!previewUrl.startsWith('/')) {
      pathOnly = `/${previewUrl}`;
    }
    
    // Determine file type from extension
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    let fileType = 'image';
    if (['pdf'].includes(extension)) {
      fileType = 'pdf';
    } else if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)) {
      fileType = 'image';
    }
    
    // Show modal with loading state first
    setPreviewModal({
      isOpen: true,
      fileUrl: '',
      signedUrl: '',
      fileName: fileName,
      fileType: fileType,
      filePath: pathOnly,
      isLoading: true
    });
    
    // Use the same secure file utility that works in ApplicationFormPage1
    try {
      const secureUrl = await getSecureFileUrl(pathOnly, applicationId);
      if (secureUrl) {
        setPreviewModal(prev => ({
          ...prev,
          signedUrl: secureUrl,
          isLoading: false
        }));
      } else {
        console.error('[ApplicationFiles] Failed to get secure URL for:', pathOnly);
        setPreviewModal(prev => ({
          ...prev,
          signedUrl: '',
          isLoading: false
        }));
      }
    } catch (error) {
      console.error('[ApplicationFiles] Error getting secure URL:', error);
      setPreviewModal(prev => ({
        ...prev,
        signedUrl: '',
        isLoading: false
      }));
    }
  };
  
  // Handle opening file in new tab with signed URL
  const handleOpenInNewTab = () => {
    // Use the already-fetched signed URL from modal state
    if (previewModal.signedUrl) {
      window.open(previewModal.signedUrl, '_blank');
    }
  };

  // Handle single file download
  const handleDownload = (downloadUrl: string) => {
    // Get the URL path without domain
    let pathOnly = downloadUrl;
    
    // Extract path if it's a full URL
    if (downloadUrl.includes('://')) {
      // Get just the path portion
      const url = new URL(downloadUrl);
      pathOnly = url.pathname;
    } else if (!downloadUrl.startsWith('/')) {
      // Ensure path starts with /
      pathOnly = `/${downloadUrl}`;
    }
    
    // Use the API_BASE from environment variable - ensures this works on any domain
    const correctUrl = `${API_BASE}${pathOnly}`;
    
    window.location.href = correctUrl;
  };

  // Handle all files download as zip
  const handleDownloadAll = () => {
    if (files.length > 0) {
      // Use forced backend URL to ensure correct API endpoint
      const downloadAllUrl = `${FORCE_BACKEND_URL}/api/applications/${applicationId}/files/download-all`;
      window.location.href = downloadAllUrl;
    }
  };
  
  // Handle file replacement
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploadingField(fieldName);
    setUploadStatus(prev => ({ ...prev, [fieldName]: 'uploading' }));
    setUploadProgress(prev => ({ ...prev, [fieldName]: 0 }));
    setUploadMessages(prev => ({ ...prev, [fieldName]: 'Uploading...' }));
    
    uploadFormFile(applicationId, file, fieldName, (progressEvent) => {
      if (progressEvent.lengthComputable) {
        const percentComplete = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(prev => ({ ...prev, [fieldName]: percentComplete }));
      }
    })
    .then(response => {
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'success' }));
      setUploadMessages(prev => ({ ...prev, [fieldName]: 'File replaced successfully!' }));
      
      // Update the file in the files array
      setFiles(prevFiles => prevFiles.map(file => {
        if (file.fieldName === fieldName) {
          return {
            ...file,
            filePath: response.filePath,
            filename: response.filePath.split('/').pop() || file.filename
          };
        }
        return file;
      }));
      
      // Clear the message after a delay (1 second)
      setTimeout(() => {
        setUploadMessages(prev => ({ ...prev, [fieldName]: '' }));
        setUploadStatus(prev => ({ ...prev, [fieldName]: 'idle' }));
        setUploadingField(null);
        
        // Refresh the files list to get updated URLs after upload
        if (applicationId) {
          fetchFiles();
        }
      }, 1000);
    })
    .catch(error => {
      console.error('Error replacing file:', error);
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'error' }));
      setUploadMessages(prev => ({ ...prev, [fieldName]: 'Failed to replace file. Please try again.' }));
      
      // Clear the error message after a longer delay
      setTimeout(() => {
        setUploadMessages(prev => ({ ...prev, [fieldName]: '' }));
        setUploadStatus(prev => ({ ...prev, [fieldName]: 'idle' }));
        setUploadingField(null);
      }, 5000);
    });
  };

  // Render loading state
  if (loading) {
    return (
      <Container>
        <Title>Application Files</Title>
        <LoadingState>
          <div style={{ fontSize: '2rem' }}>⏳</div>
          <p>Loading your files...</p>
        </LoadingState>
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container>
        <Title>Application Files</Title>
        <LoadingState>
          <div style={{ fontSize: '2rem' }}>⚠️</div>
          <p>{error}</p>
        </LoadingState>
      </Container>
    );
  }

  // Render empty state
  if (files.length === 0) {
    return (
      <Container>
        <Title>Application Files</Title>
        <NoFiles>
          <div style={{ fontSize: '2rem' }}>📁</div>
          <p>No files found for this application</p>
        </NoFiles>
      </Container>
    );
  }

  // Render files
  return (
    <Container>
      <Title>Application Files ({files.length})</Title>
      
      <FilesGrid>
        {sortedFiles.map((file, index) => (
          <FileCard 
            key={file.fieldName}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <FileLabel>
              <span style={{ 
                background: '#4c6ef5', 
                color: 'white', 
                borderRadius: '50%', 
                width: '24px', 
                height: '24px', 
                display: 'inline-flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                marginRight: '8px',
                fontSize: '0.8rem',
                fontWeight: 'bold'
              }}>{index + 1}</span>
              {file.fieldLabel}
            </FileLabel>
            <FileName title={file.filename}>{file.filename}</FileName>
            
            {/* Display upload progress if file is being uploaded */}
            {uploadStatus[file.fieldName] === 'uploading' && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{
                  height: '6px',
                  width: '100%',
                  backgroundColor: '#e2e8f0',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${uploadProgress[file.fieldName] || 0}%`,
                    backgroundColor: '#4c6ef5',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  color: '#64748b',
                  marginTop: '0.25rem'
                }}>
                  {uploadProgress[file.fieldName] || 0}%
                </div>
              </div>
            )}
            
            {/* Display success/error message if any */}
            {uploadMessages[file.fieldName] && (
              <div style={{
                fontSize: '0.75rem',
                color: uploadStatus[file.fieldName] === 'error' ? '#ef4444' : '#10b981',
                marginBottom: '0.5rem'
              }}>
                {uploadMessages[file.fieldName]}
              </div>
            )}
            
            <ButtonGroup>
              <ActionButton 
                onClick={() => handlePreview(file.previewUrl, file.filename)}
                whileHover={{ scale: 1.05 }}
                title="Preview file"
                disabled={uploadStatus[file.fieldName] === 'uploading'}
              >
                <VisibilityIcon />
              </ActionButton>
              <ActionButton 
                onClick={() => {
                  // Direct override: always use port 5000 instead of 3000
                  const path = file.downloadUrl.includes('/uploads/') ? 
                    `/uploads/${file.downloadUrl.split('/uploads/')[1]}` : 
                    file.downloadUrl;
                  
                  // Use the forced backend URL which adapts between localhost and production
                  const url = `${FORCE_BACKEND_URL}${path}`;
                  console.log('Downloading file with URL:', url);
                  window.location.href = url;
                }}
                whileHover={{ scale: 1.05 }}
                title="Download file"
                disabled={uploadStatus[file.fieldName] === 'uploading'}
              >
                <DownloadIcon />
              </ActionButton>
              
              {/* File upload input for replacement - disabled when application is locked */}
              <label htmlFor={`file-upload-${file.fieldName}`}>
                <UploadInput
                  id={`file-upload-${file.fieldName}`}
                  type="file"
                  onChange={(e) => handleFileChange(e, file.fieldName)}
                  disabled={isApplicationLocked || uploadStatus[file.fieldName] === 'uploading' || uploadingField !== null}
                />
                <ReplaceFileButton 
                  className={isApplicationLocked || uploadStatus[file.fieldName] === 'uploading' || uploadingField !== null ? 'disabled' : ''}
                  title={isApplicationLocked ? "Application is locked - file replacement disabled" : "Replace file"}
                >
                  <UploadFileIcon fontSize="small" />
                  <span>Replace file</span>
                </ReplaceFileButton>
              </label>
            </ButtonGroup>
          </FileCard>
        ))}
      </FilesGrid>
      
      {files.length > 1 && (
        <ZipDownloadButton 
          onClick={handleDownloadAll}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FileDownloadIcon /> Download All Files
        </ZipDownloadButton>
      )}
      
      {/* File Preview Modal */}
      {previewModal.isOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setPreviewModal({ isOpen: false, fileUrl: '', signedUrl: '', fileName: '', fileType: '', filePath: '', isLoading: false })}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '90vw',
              maxHeight: '90vh',
              width: 'auto',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#4c6ef5',
              borderRadius: '12px 12px 0 0'
            }}>
              <h3 style={{ 
                margin: 0, 
                color: 'white', 
                fontWeight: 600,
                fontSize: '1rem',
                maxWidth: '400px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {previewModal.fileName}
              </h3>
              <button
                onClick={() => setPreviewModal({ isOpen: false, fileUrl: '', signedUrl: '', fileName: '', fileType: '', filePath: '', isLoading: false })}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: 'white',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  lineHeight: 1
                }}
              >
                ✕
              </button>
            </div>
            
            {/* Modal Content */}
            <div style={{
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
              maxHeight: 'calc(90vh - 140px)',
              backgroundColor: '#f8fafc',
              minHeight: '200px'
            }}>
              {previewModal.isLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '3px solid #e5e7eb',
                    borderTop: '3px solid #4c6ef5',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 15px auto'
                  }} />
                  <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                  <p style={{ color: '#6b7280', margin: 0 }}>Loading secure preview...</p>
                </div>
              ) : previewModal.fileType === 'pdf' ? (
                <iframe
                  src={previewModal.signedUrl}
                  style={{
                    width: '100%',
                    minWidth: '600px',
                    height: '70vh',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                  title={previewModal.fileName}
                />
              ) : (
                <img
                  src={previewModal.signedUrl}
                  alt={previewModal.fileName}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
              )}
            </div>
            
            {/* Modal Footer with Download button */}
            <div style={{
              padding: '12px 20px',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: 'white',
              display: 'flex',
              justifyContent: 'center',
              gap: '12px'
            }}>
              <button
                onClick={handleOpenInNewTab}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4c6ef5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <DownloadIcon fontSize="small" /> Open in New Tab
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default ApplicationFiles;
